import React from "react";
import { Target, Users, Lightbulb, PlayCircle, FileCheck, MessageSquare, Settings } from "lucide-react";
import { mockAgents } from "../data/mock";

const AgentSystem = () => {
  const iconMap = {
    Target: Target,
    Users: Users,
    Lightbulb: Lightbulb,
    PlayCircle: PlayCircle,
    FileCheck: FileCheck,
    MessageSquare: MessageSquare,
    Settings: Settings
  };

  return (
    <section 
      id="agents" 
      className="py-20 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(rgba(255, 244, 232, 0.95), rgba(255, 244, 232, 0.9)), url("https://images.unsplash.com/photo-1672581437674-3186b17b405a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NTk5MzM5MDV8MA&ixlib=rb-4.1.0&q=85")',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="container relative z-10">
        {/* AI Agent imagery */}
        <div className="absolute top-16 right-4 opacity-30 hidden xl:block">
          <img 
            src="https://images.unsplash.com/photo-1737644467636-6b0053476bb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxBSSUyMHJvYm90c3xlbnwwfHx8fDE3NTk5MzM4MTB8MA&ixlib=rb-4.1.0&q=85" 
            alt="AI Agent"
            className="w-48 h-64 object-cover rounded-lg"
          />
        </div>
        <div className="text-center mb-16">
          <div className="hero-announcement mb-6">
            <span style={{ color: 'var(--accent-green-200)' }}>✓ COMPREHENSIVE SYSTEM</span>
            <span>6-Agent AI Architecture for Complete Business Automation</span>
          </div>
          <h2 className="heading-1 mb-4">
            The 6-Agent System for 
            <span style={{ color: 'var(--accent-orange-400)' }}> Business Growth</span>
          </h2>
          <p className="body-large" style={{ 
            maxWidth: '700px', 
            margin: '0 auto',
            color: 'var(--text-primary)',
            fontWeight: '500'
          }}>
            Each AI agent handles a specific part of your customer journey, working together to:
            <strong> generate leads, qualify prospects, close deals, and deliver exceptional service.</strong>
          </p>
        </div>

        <div className="voice-grid max-w-6xl mx-auto">
          {mockAgents.map((agent, index) => {
            const IconComponent = iconMap[agent.icon];
            const accentClass = agent.accentColor;
            
            return (
              <div key={index} className={`voice-card hover-lift ${accentClass}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 rounded-full" style={{ 
                    background: 'rgba(255, 255, 255, 0.7)',
                    color: 'var(--text-primary)' 
                  }}>
                    <IconComponent size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="voice-card-title">
                      Agent #{agent.number}: {agent.name}
                    </h3>
                  </div>
                </div>
                
                <div className="voice-card-description mb-4">
                  <div className="body-small mb-3" style={{ fontWeight: '500' }}>
                    Triggers: {agent.triggers}
                  </div>
                  <div className="body-small mb-3">
                    <strong>Actions:</strong> {agent.actions}
                  </div>
                  <div className="body-small italic">
                    "{agent.personality}"
                  </div>
                </div>

                <div className="mt-auto">
                  <div className="flex flex-wrap gap-2">
                    {agent.features.map((feature, featureIndex) => (
                      <span 
                        key={featureIndex}
                        className="mono-text px-3 py-1 rounded-full text-xs"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.6)',
                          color: 'var(--text-muted)' 
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <div className="inline-flex flex-col items-center gap-4 p-8 rounded-lg relative overflow-hidden" style={{ 
            background: 'var(--bg-card)',
            border: '2px solid var(--accent-green-200)'
          }}>
            {/* AI Success visualization */}
            <div className="absolute top-4 right-4 opacity-20">
              <img 
                src="https://images.unsplash.com/photo-1673255745677-e36f618550d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxBSSUyMHJvYm90c3xlbnwwfHx8fDE3NTk5MzM4MTB8MA&ixlib=rb-4.1.0&q=85" 
                alt="AI Collaboration"
                className="w-24 h-32 object-cover rounded-lg"
              />
            </div>
            <div className="heading-3 mb-2 relative z-10" style={{ color: 'var(--accent-green-200)' }}>
              Ready to Transform Your Business with AI?
            </div>
            <p className="body-medium mb-4 relative z-10" style={{ color: 'var(--text-secondary)' }}>
              Implement this comprehensive 6-agent system in your business. 30-day deployment timeline.
            </p>
            <button className="btn-primary hover-scale text-lg px-8 py-4 relative z-10">
              Learn More About Our AI System
            </button>
            <div className="caption relative z-10" style={{ color: 'var(--text-muted)' }}>
              Professional implementation • Comprehensive training included
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgentSystem;